<template>
  <v-main>
    <v-container fluid>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-2 pt-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h6 font-weight-bold primary--text mb-0">
                    Referrals
                  </h2>
                  <p class="text-caption grey--text ma-0 mt-1">
                    Users who signed up using {{ patientName || "this patient" }}'s referral code
                  </p>
                  <v-chip color="primary" small class="mt-2">
                    <v-icon left small>mdi-ticket-percent-outline</v-icon>
                    Referral Code: {{ patientReferralCode || "—" }}
                  </v-chip>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn color="primary" outlined small @click="$router.push('/patients')">
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Patients
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-account-group</v-icon>
                    <span class="text-body-1 font-weight-medium">Referrals List</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0 text-right">
                  <v-chip color="primary" outlined x-small>
                    <v-icon left x-small>mdi-account-group</v-icon>
                    {{ referrals.length }} Referred
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                <p class="mt-3 grey--text">Loading referrals...</p>
              </div>

              <v-data-table
                v-else
                :headers="headers"
                :items="referrals"
                hide-default-footer
                :items-per-page="-1"
                class="patient-referrals-table"
              >
                <!-- # -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">{{ index + 1 }}</span>
                </template>

                <!-- Name -->
                <template #[`item.name`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="grey lighten-2">
                      <v-img v-if="item.image_path" :src="item.image_path"></v-img>
                      <v-icon v-else size="18" color="grey darken-1">mdi-account</v-icon>
                    </v-avatar>
                    <span class="text-body-2 font-weight-medium">
                      {{ referredUserName(item) }}
                    </span>
                  </div>
                </template>

                <!-- Contact -->
                <template #[`item.contact`]="{ item }">
                  <div class="text-caption">
                    <div v-if="item.phone" class="d-flex align-center">
                      <v-icon x-small class="mr-1 grey--text">mdi-phone</v-icon>{{ item.phone }}
                    </div>
                    <div v-if="item.email" class="d-flex align-center">
                      <v-icon x-small class="mr-1 grey--text">mdi-email</v-icon>{{ item.email }}
                    </div>
                  </div>
                </template>

                <!-- Position -->
                <template #[`item.position`]="{ item }">
                  <v-chip small outlined>
                    {{ item.position ? item.position.name : "—" }}
                  </v-chip>
                </template>

                <!-- Joined On -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    <div class="font-weight-medium">{{ formatDate(item.created_at) }}</div>
                    <div class="grey--text">{{ formatTime(item.created_at) }}</div>
                  </div>
                </template>

                <!-- Empty state -->
                <template #no-data>
                  <div class="text-center py-10">
                    <v-icon size="60" color="grey lighten-3">mdi-account-group-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-3 mb-2">No Referrals Found</h3>
                    <p class="grey--text">No one has signed up using this patient's referral code yet.</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "PatientReferralsIndex",
  data() {
    return {
      referrals: [],
      loading: false,
      patientReferralCode: "",
      headers: [
        { text: "#",         value: "sr_no",     sortable: false, width: "50px", align: "center" },
        { text: "Name",      value: "name",      sortable: false, width: "220px" },
        { text: "Contact",   value: "contact",   sortable: false, width: "220px" },
        { text: "Position",  value: "position",  sortable: false, width: "140px", align: "center" },
        { text: "Joined On", value: "created_at", sortable: false, width: "140px" },
      ],
    };
  },
  computed: {
    patientName() {
      return this.$route.query.patient_name || "";
    },
  },
  mounted() {
    this.getData();
    this.getPatientReferralCode();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        const patientId = this.decryptIt(this.$route.params.id);
        const res = await axios.get(`/users?referred_user_id=${patientId}&context=panel`);
        this.referrals = res.data.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async getPatientReferralCode() {
      try {
        const res = await axios.get(`/users/${this.$route.params.id}?context=panel`);
        this.patientReferralCode = res.data.data?.referral_code || "";
      } catch (e) {
        console.error(e);
      }
    },
    referredUserName(item) {
      const name = `${item.first_name || ""} ${item.last_name || ""}`.trim();
      return name || item.user_name || "Unknown User";
    },
    formatDate(dt) {
      if (!dt) return "—";
      return new Date(dt).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    },
    formatTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },
  },
};
</script>

<style scoped>
.patient-referrals-table >>> .v-data-table__wrapper table thead tr th {
  background: #f5f7fa;
  font-size: 0.75rem;
  font-weight: 600;
  color: #455a64;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.patient-referrals-table >>> .v-data-table__wrapper table tbody tr:hover {
  background: #f8f9fe !important;
}
</style>
